'use client';

import React, { useState, useMemo } from 'react';
import {
    History,
    Search,
    Download,
    Eye,
    CheckCircle2,
    Clock,
    ArrowLeft,
    Receipt,
    CreditCard,
    Banknote,
    QrCode,
    Printer,
    Share2,
    AlertCircle
} from 'lucide-react';

interface PaymentHistory {
    id: string;
    invoiceId: string;
    description: string;
    amount: number;
    date: string;
    method: string;
    status: 'Success' | 'Pending' | 'Failed';
    referenceNumber: string;
    bankName?: string;
}

export default function RiwayatPembayaranPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedHistory, setSelectedHistory] = useState<PaymentHistory | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');

    const historyData: PaymentHistory[] = [
        {
            id: 'TRX-88291-JK',
            invoiceId: 'INV-2026-002',
            description: 'Pembayaran SPP Januari 2026',
            amount: 750000,
            date: '04 Jan 2026, 14:20',
            method: 'Virtual Account BNI',
            status: 'Success',
            referenceNumber: 'REF/BNI/20260104/88921',
            bankName: 'Bank Negara Indonesia (BNI)'
        },
        {
            id: 'TRX-85521-LM',
            invoiceId: 'INV-2025-088',
            description: 'Pembayaran Uang Buku Semester Genap',
            amount: 1250000,
            date: '20 Jan 2026, 09:15',
            method: 'QRIS (GOPAY)',
            status: 'Success',
            referenceNumber: 'QR/GPY/20260120/5521',
        },
        {
            id: 'TRX-77401-OP',
            invoiceId: 'INV-2025-102',
            description: 'Uang Seragam Olahraga Baru',
            amount: 450000,
            date: '15 Dec 2025, 11:05',
            method: 'Transfer Manual BSI',
            status: 'Success',
            referenceNumber: 'MAN/BSI/20251215/774',
            bankName: 'Bank Syariah Indonesia (BSI)'
        },
        {
            id: 'TRX-90122-PQ',
            invoiceId: 'INV-2026-001',
            description: 'Pembayaran SPP Februari 2026',
            amount: 750000,
            date: '09 Feb 2026, 18:30',
            method: 'Virtual Account Mandiri',
            status: 'Pending',
            referenceNumber: 'REF/MDR/20260209/9012',
            bankName: 'Bank Mandiri'
        }
    ];

    const filteredHistory = useMemo(() => {
        return historyData.filter(item =>
            item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.invoiceId.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const handleViewDetail = (item: PaymentHistory) => {
        setSelectedHistory(item);
        setViewMode('detail');
    };

    const handleBack = () => {
        setViewMode('list');
        setSelectedHistory(null);
    };

    const StatusBadge = ({ status }: { status: PaymentHistory['status'] }) => (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${status === 'Success' ? 'bg-success-soft text-success border-success/10' :
                status === 'Pending' ? 'bg-warning-soft text-warning border-warning/10' :
                    'bg-error-soft text-error border-error/10'
            }`}>
            <div className={`w-1 h-1 rounded-full ${status === 'Success' ? 'bg-success' : status === 'Pending' ? 'bg-warning' : 'bg-error'}`}></div>
            {status}
        </div>
    );

    const ListView = () => (
        <div className="space-y-10 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight flex items-center gap-4">
                        <History className="w-8 h-8 text-primary" /> Riwayat Pembayaran
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Lacak seluruh transaksi pembayaran yang pernah Anda lakukan sebelumnya.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari transaksi..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-11 pr-5 py-3.5 bg-surface border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 outline-none w-full md:w-64"
                        />
                    </div>
                </div>
            </div>

            {/* History Table */}
            <div className="bg-surface border border-border-main rounded-[32px] overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">ID Transaksi & Tanggal</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Keterangan</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Metode</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] text-right">Nominal</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredHistory.length > 0 ? filteredHistory.map((item) => (
                                <tr
                                    key={item.id}
                                    className="hover:bg-primary-soft/30 transition-colors group cursor-pointer"
                                    onClick={() => handleViewDetail(item)}
                                >
                                    <td className="px-8 py-6">
                                        <div className="space-y-1">
                                            <p className="text-sm font-black text-foreground tracking-tight uppercase group-hover:text-primary transition-colors">{item.id}</p>
                                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-none">{item.date}</p>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-foreground">{item.description}</p>
                                            <p className="text-[9px] font-black text-primary uppercase tracking-wider">{item.invoiceId}</p>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-background border border-border-main flex items-center justify-center text-muted">
                                                {item.method.includes('Account') ? <CreditCard className="w-4 h-4" /> :
                                                    item.method.includes('QRIS') ? <QrCode className="w-4 h-4" /> :
                                                        <Banknote className="w-4 h-4" />}
                                            </div>
                                            <span className="text-[11px] font-bold text-secondary uppercase whitespace-nowrap">{item.method}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-center whitespace-nowrap">
                                        <StatusBadge status={item.status} />
                                    </td>
                                    <td className="px-8 py-6 text-right whitespace-nowrap">
                                        <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                    </td>
                                    <td className="px-8 py-6 text-right whitespace-nowrap">
                                        <button className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted group-hover:text-primary group-hover:border-primary transition-all shadow-xs">
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-20 text-center">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-16 h-16 rounded-full bg-background border border-dashed border-border-main flex items-center justify-center text-muted">
                                                <History className="w-8 h-8" />
                                            </div>
                                            <p className="text-sm text-secondary font-bold uppercase tracking-widest">Belum ada riwayat transaksi</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Footer Table */}
                <div className="px-8 py-6 bg-background/30 border-t border-border-main flex justify-between items-center">
                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Total {filteredHistory.length} Transaksi Ditemukan</p>
                    <div className="flex gap-2">
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Previous</button>
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Next Page</button>
                    </div>
                </div>
            </div>
        </div>
    );

    const DetailView = () => {
        if (!selectedHistory) return null;
        return (
            <div className="max-w-7xl mx-auto space-y-8 animate-in slide-in-from-right duration-500">
                <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary font-bold text-xs uppercase tracking-widest transition-colors mb-4"
                >
                    <ArrowLeft className="w-4 h-4" /> Kembali ke Riwayat
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left: Receipt Card */}
                    <div className="lg:col-span-8">
                        <div className="bg-surface border border-border-main rounded-[40px] shadow-sm overflow-hidden relative">
                            {/* Receipt Header Decoration */}
                            <div className="absolute top-0 left-0 right-0 h-2 bg-primary"></div>

                            <div className="p-10 border-b border-border-main flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-2xl bg-primary-soft flex items-center justify-center text-primary">
                                        <Receipt className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Struk Pembayaran Digital</p>
                                        <h2 className="text-2xl font-black text-foreground tracking-tight">{selectedHistory.id}</h2>
                                    </div>
                                </div>
                                <StatusBadge status={selectedHistory.status} />
                            </div>

                            <div className="p-10 space-y-10">
                                {/* Details Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                                    <div className="space-y-4">
                                        <div className="pb-2 border-b border-border-main/50">
                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Waktu Transaksi</p>
                                            <p className="text-sm font-bold text-foreground mt-1">{selectedHistory.date}</p>
                                        </div>
                                        <div className="pb-2 border-b border-border-main/50">
                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Metode Pembayaran</p>
                                            <p className="text-sm font-bold text-foreground mt-1">{selectedHistory.method}</p>
                                        </div>
                                        {selectedHistory.bankName && (
                                            <div className="pb-2 border-b border-border-main/50">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Bank Penerima</p>
                                                <p className="text-sm font-bold text-foreground mt-1">{selectedHistory.bankName}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-4">
                                        <div className="pb-2 border-b border-border-main/50">
                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">ID Tagihan</p>
                                            <p className="text-sm font-black text-primary mt-1">{selectedHistory.invoiceId}</p>
                                        </div>
                                        <div className="pb-2 border-b border-border-main/50">
                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Nomor Referensi</p>
                                            <p className="text-sm font-bold text-foreground mt-1 tabular-nums">{selectedHistory.referenceNumber}</p>
                                        </div>
                                        <div className="pb-2 border-b border-border-main/50">
                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Status Verifikasi</p>
                                            <p className="text-sm font-bold text-foreground mt-1 flex items-center gap-2">
                                                {selectedHistory.status === 'Success' ? (
                                                    <><CheckCircle2 className="w-4 h-4 text-success" /> Terverifikasi Sistem</>
                                                ) : (
                                                    <><Clock className="w-4 h-4 text-warning" /> Menunggu Konfirmasi Bank</>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Summary Box */}
                                <div className="bg-background rounded-[32px] p-8 border border-border-main border-dashed space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-bold text-muted-foreground">Deskripsi Pembayaran</span>
                                        <span className="text-sm font-bold text-foreground text-right">{selectedHistory.description}</span>
                                    </div>
                                    <div className="h-px bg-border-main/50 w-full"></div>
                                    <div className="flex justify-between items-center pt-2">
                                        <span className="text-lg font-black text-foreground uppercase tracking-tight">Total Dibayar</span>
                                        <span className="text-2xl font-black text-primary tabular-nums">{formatCurrency(selectedHistory.amount)}</span>
                                    </div>
                                </div>

                                {/* Security Note */}
                                <div className="flex items-start gap-4 p-6 bg-primary/5 rounded-3xl border border-primary/10">
                                    <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                    <p className="text-xs text-primary/80 font-medium leading-relaxed">
                                        Ini adalah dokumen resmi bukti pembayaran elektronik yang sah dan diakui oleh pihak sekolah. Simpan struk ini sebagai referensi jika diperlukan di masa mendatang.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Actions Sidebar */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-surface border border-border-main rounded-[40px] p-8 shadow-sm space-y-4 sticky top-28">
                            <h3 className="text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-6">Opsi & Bagikan</h3>

                            <button className="w-full flex items-center justify-center gap-3 bg-primary text-white font-black py-4 rounded-2xl shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all group">
                                <Download className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" /> Unduh PDF
                            </button>

                            <button className="w-full flex items-center justify-center gap-3 bg-white border border-border-main text-foreground font-black py-4 rounded-2xl hover:bg-surface transition-all">
                                <Printer className="w-5 h-5" /> Cetak Bukti
                            </button>

                            <button className="w-full flex items-center justify-center gap-3 bg-white border border-border-main text-foreground font-black py-4 rounded-2xl hover:bg-surface transition-all">
                                <Share2 className="w-5 h-5" /> Bagikan
                            </button>

                            <div className="pt-6 border-t border-border-main mt-6">
                                <p className="text-[9px] text-muted-foreground font-medium text-center leading-relaxed">
                                    ID Transaksi: {selectedHistory.id}<br />
                                    Diajukan pada {selectedHistory.date}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <main className="max-w-7xl mx-auto w-full p-4 md:p-10 pb-32">
            {viewMode === 'list' && <ListView />}
            {viewMode === 'detail' && <DetailView />}
        </main>
    );
}
